<template>
    <div>
        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="search" placeholder="Buscar por empresa, processo, CNPJ, vara ou juiz" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"
                    @input="debouncedLoad"></v-text-field>
                <v-select v-if="scope !== 'triage'" v-model="status" :items="statusItems" label="Etapa" dense outlined hide-details clearable style="max-width: 190px;" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-select v-model="uf" :items="ufs" label="UF" dense outlined hide-details clearable style="max-width: 110px;" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-select v-model="type" :items="typeItems" label="Tipo" dense outlined hide-details clearable style="max-width: 110px;" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-select v-model="owner" :items="ownerItems" label="Dono" dense outlined hide-details clearable style="max-width: 170px;" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <!-- Faixa de passivo (ou valor da causa); caso sem valor entra em "Acima de R$ 10 mi" -->
                <v-select v-model="amountBand" :items="amountBandItems" label="Passivo" dense outlined hide-details clearable style="max-width: 190px;" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-btn outlined small class="pc-export" :disabled="!items.length" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
            </template>

            <div v-if="canTriage && selected.length" class="pc-bulk">
                <span class="ui-small"><strong>{{ selected.length }}</strong> {{ selected.length === 1 ? "selecionado" : "selecionados" }}</span>
                <v-btn v-if="$allowed('prospect.triage')" small outlined :disabled="busy" @click="triage('acompanhar')"><v-icon left small>mdi-eye-outline</v-icon>Acompanhar</v-btn>
                <v-btn v-if="$allowed('prospect.triage')" small outlined :disabled="busy" @click="triage('passivo_baixo')">Passivo baixo</v-btn>
                <v-btn v-if="$allowed('prospect.triage')" small outlined :disabled="busy" @click="triage('descartado')">Descartar</v-btn>
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small outlined :disabled="busy" v-bind="attrs" v-on="on"><v-icon left small>mdi-account-arrow-right-outline</v-icon>Atribuir dono</v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item v-for="person in team" :key="person.id" @click="assign(person.id)"><v-list-item-title>{{ person.name }}</v-list-item-title></v-list-item>
                        <v-divider></v-divider>
                        <v-list-item @click="assign(null)"><v-list-item-title class="ui-muted">Sem dono</v-list-item-title></v-list-item>
                    </v-list>
                </v-menu>
                <span class="ui-small ui-muted">Corte de passivo baixo: {{ formatMoneyShort(threshold) }}</span>
            </div>

            <!-- Busca fora da aba: casos de outras abas e linhas descartadas nas importacoes -->
            <div v-if="extras.other_cases.length || extras.discarded_rows.length" class="pc-extras">
                <div v-if="extras.other_cases.length" class="pc-extras__group">
                    <div class="pc-extras__title"><v-icon small>mdi-folder-search-outline</v-icon>Também encontrado em outras abas</div>
                    <button v-for="item in extras.other_cases" :key="`case-${item.id}`" type="button" class="pc-extras__row" @click="open(item)">
                        <span><strong>{{ item.company_name || "—" }}</strong> <span class="ui-mono ui-muted">· {{ item.process_number }}</span></span>
                        <span class="ui-small ui-muted">{{ item.type_label }} · {{ item.pipeline_status_label }}</span>
                    </button>
                </div>
                <div v-if="extras.discarded_rows.length" class="pc-extras__group">
                    <div class="pc-extras__title"><v-icon small>mdi-archive-search-outline</v-icon>Descartado na importação (não virou caso)</div>
                    <div v-for="row in extras.discarded_rows" :key="`row-${row.id}`" class="pc-extras__row pc-extras__row--static">
                        <span>
                            <strong>{{ row.company_name || "—" }}</strong> <span class="ui-mono ui-muted">· {{ row.process_number }}</span>
                            <div class="ui-small ui-muted">{{ row.reason_label }}<span v-if="row.process_class"> · {{ row.process_class }}</span> · importação #{{ row.import_id }} de {{ row.imported_at | defaultDate("DD/MM/YYYY") }}</div>
                        </span>
                        <v-btn v-if="$allowed('prospect.import')" small outlined :disabled="busy" @click="openRescue(row)"><v-icon left small>mdi-tray-arrow-up</v-icon>Trazer para a triagem</v-btn>
                    </div>
                </div>
            </div>

            <v-data-table v-model="selected" :headers="visibleHeaders" :items="items" :loading="loading" disable-sort hide-default-footer :items-per-page="-1" :show-select="canTriage" item-key="id"
                :no-data-text="emptyText" loading-text="Carregando..." class="ui-clickable-rows" @click:row="open">
                <template v-slot:[`item.company_name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 180px;">{{ item.company_name || "—" }}</div>
                    <div class="ui-cell-sub ui-mono">{{ item.process_number }}<span v-if="item.company_document && !compact"> · {{ formatDocument(item.company_document) }}</span></div>
                    <div v-if="compact" class="mt-1"><ui-badge set="prospect-type" :status="item.type" /><span v-if="item.needs_review" class="ui-cell-sub"> da revisão</span></div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <ui-badge set="prospect-type" :status="item.type" />
                    <div v-if="item.needs_review" class="ui-cell-sub">da revisão</div>
                </template>
                <template v-slot:[`item.court_name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 180px;">{{ item.uf || "—" }} · {{ item.court_name || item.comarca || "—" }}</div>
                    <div class="ui-cell-sub">{{ item.judge_name || "juiz não informado" }}</div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <span class="ui-nowrap" :class="{ 'ui-muted': item.below_threshold }">{{ formatMoneyShort(item.amount) }}</span>
                    <div class="ui-cell-sub">{{ item.amount_source === "passivo" ? "passivo" : (item.amount ? "valor da causa" : "") }}</div>
                </template>
                <template v-slot:[`item.pipeline_status`]="{ item }">
                    <ui-badge set="prospect-pipeline" :status="item.pipeline_status" />
                    <!-- Desativado (29/10/2026): "sem dono" deixou de ser pendência; mostra o dono só quando há. -->
                    <div v-if="item.owner" class="ui-cell-sub">dono: {{ item.owner.name }}</div>
                    <div v-if="compact" class="ui-cell-sub">importado {{ item.created_at | defaultDate("DD/MM") }} · cadastro {{ formatFeedDate(item.captured_at) }}</div>
                </template>
                <template v-slot:[`item.captured_at`]="{ item }">
                    <span class="ui-nowrap">{{ item.created_at | defaultDate("DD/MM/YYYY") }}</span>
                    <div class="ui-cell-sub">cadastro {{ formatFeedDate(item.captured_at) }} · {{ item.source === "brzj" ? "BRZJ" : "RecuperaJur" }}</div>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
            <template #footer>
                <ui-pagination :total="total" :page.sync="page" :items-per-page.sync="perPage" />
            </template>
        </ui-table-card>

        <!-- Trazer linha descartada para a triagem: escolhe o tipo do caso -->
        <v-dialog v-model="rescue.open" max-width="460">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Trazer para a triagem</h3>
                        <p class="ui-section-description">{{ rescue.row ? `${rescue.row.company_name || "—"} · ${rescue.row.process_number}` : "" }}</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <p class="ui-small ui-muted mt-0">Foi descartado na importação ({{ rescue.row ? rescue.row.reason_label : "" }}). O caso entra em "A triar" marcado para revisão.</p>
                    <v-select v-model="rescue.type" :items="typeItems" label="Tipo do caso" outlined dense hide-details :menu-props="{ offsetY: true }"></v-select>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="busy" @click="rescue.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="busy" @click="confirmRescue">Trazer</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import {
    PIPELINE_LABELS, TYPE_ITEMS, formatDocument, formatFeedDate, formatMoneyShort, notifyError, notifySuccess,
} from "@/components/prospect/prospect.util";

// Abas "A triar", "Encerrados" e "Todos" da tela de Casos. scope e o recorte
// do backend (triage | closed | all); a triagem em lote so aparece no que
// ainda nao foi triado. Emite "changed" quando a triagem move casos de aba.
export default {
    name: "ProspectCaseTable",

    props: {
        scope: { type: String, required: true },
        tab: { type: String, required: true }, // volta da ficha para esta aba
        initialStatus: { type: String, default: null },
    },

    data: () => ({
        items: [],
        total: 0,
        page: 1,
        perPage: 50,
        loading: false,
        busy: false,
        status: null,
        search: "",
        uf: null,
        type: null,
        owner: null,
        ufs: [],
        team: [],
        threshold: 10000000,
        amountBand: null,
        amountBandItems: [
            { value: "ate_3", text: "Até R$ 3 mi" },
            { value: "de_3_a_10", text: "R$ 3 a 10 mi" },
            { value: "acima_10", text: "Acima de R$ 10 mi" },
        ],
        extras: { other_cases: [], discarded_rows: [] },
        rescue: { open: false, row: null, type: "rj" },
        selected: [],
        typeItems: TYPE_ITEMS,
        timer: null,
        headers: [
            { text: "Empresa · processo", value: "company_name" },
            { text: "Tipo", value: "type", width: 90 },
            { text: "UF · vara · juiz", value: "court_name" },
            { text: "Passivo", value: "amount", align: "end", width: 130 },
            { text: "Etapa", value: "pipeline_status", width: 170 },
            { text: "Importado", value: "captured_at", width: 130 },
            { text: "", value: "actions", align: "end", width: 40 },
        ],
    }),

    computed: {
        canTriage() {
            return this.scope === "triage";
        },
        // Tela estreita: tipo e data de captacao vao para as colunas vizinhas.
        compact() {
            return this.$vuetify.breakpoint.width < 1400;
        },
        visibleHeaders() {
            return this.compact ? this.headers.filter((header) => !["type", "captured_at"].includes(header.value)) : this.headers;
        },
        hasFilters() {
            return !!this.search || !!this.uf || !!this.type || !!this.owner || !!this.status || this.amountBand !== this.defaultBand;
        },
        ownerItems() {
            return [{ value: "none", text: "Sem dono" }, ...this.team.map((person) => ({ value: person.id, text: person.name }))];
        },
        statusItems() {
            return Object.entries(PIPELINE_LABELS).map(([value, text]) => ({ value, text }));
        },
        // "A triar" abre so com os relevantes (pedido de 23/10/2026); as outras abas, com tudo.
        defaultBand() {
            return this.scope === "triage" ? "acima_10" : null;
        },
        emptyText() {
            return { triage: "Nada a triar. Os casos novos chegam pelas importações.", closed: "Nenhum caso encerrado neste filtro." }[this.scope] || "Nenhum caso neste filtro";
        },
    },

    watch: {
        page() {
            this.load();
        },
        perPage() {
            this.reload();
        },
    },

    created() {
        this.status = this.initialStatus;
        this.amountBand = this.defaultBand;
        this.load();
        this.loadTeam();
    },

    methods: {
        formatDocument,
        formatFeedDate,
        formatMoneyShort,

        params() {
            return {
                page: this.page,
                perPage: this.perPage,
                scope: this.scope === "all" ? undefined : this.scope,
                status: this.status || undefined,
                search: this.search || undefined,
                uf: this.uf || undefined,
                type: this.type || undefined,
                owner: this.owner || undefined,
                amount_band: this.amountBand || undefined,
            };
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/prospect/cases", { params: this.params() });
                const content = result.data.content;
                this.items = content.data;
                this.total = content.total;
                this.ufs = content.ufs;
                this.threshold = content.settings ? content.settings.low_debt_threshold : this.threshold;
                this.extras = content.search_extras || { other_cases: [], discarded_rows: [] };
                this.$emit("counts", content.scope_counts || null);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async loadTeam() {
            try {
                const result = await axios.get("/api/admin/prospect/team");
                this.team = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        reload() {
            if (this.page !== 1) this.page = 1;
            else this.load();
        },

        debouncedLoad() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.reload(), 350);
        },

        clearFilters() {
            this.search = "";
            this.uf = null;
            this.type = null;
            this.owner = null;
            this.status = null;
            this.amountBand = this.defaultBand;
            this.reload();
        },

        open(item) {
            this.$router.push({ path: `/prospeccao/casos/${item.id}`, query: { from: this.tab } });
        },

        openRescue(row) {
            this.rescue = { open: true, row, type: "rj" };
        },

        async confirmRescue() {
            if (!this.rescue.row) return;
            this.busy = true;
            try {
                const row = this.rescue.row;
                const result = await axios.post(`/api/admin/prospect/imports/${row.import_id}/rows/${row.id}/rescue`, { type: this.rescue.type });
                notifySuccess(this, "Caso trazido para a triagem.");
                this.rescue.open = false;
                this.open({ id: result.data.content.id });
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },

        async triage(status) {
            await this.applyTriage({ status }, `${this.selected.length === 1 ? "Caso marcado" : "Casos marcados"} como "${PIPELINE_LABELS[status]}".`);
        },

        async assign(ownerId) {
            await this.applyTriage({ owner_admin_id: ownerId }, ownerId ? "Dono atribuído." : "Dono removido.");
        },

        async applyTriage(payload, message) {
            if (!this.selected.length) return;
            this.busy = true;
            try {
                await axios.post("/api/admin/prospect/cases/triage", { ids: this.selected.map((item) => item.id), ...payload });
                notifySuccess(this, message);
                this.selected = [];
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },

        exportRows() {
            downloadXlsx(this.items.map((item) => ({
                Processo: item.process_number,
                Tipo: item.type_label,
                Empresa: item.company_name || "",
                CNPJ: formatDocument(item.company_document),
                UF: item.uf || "",
                Comarca: item.comarca || "",
                Vara: item.court_name || "",
                Juiz: item.judge_name || "",
                Passivo: item.debt_amount,
                "Valor da causa": item.claim_amount,
                Etapa: item.pipeline_status_label,
                Dono: item.owner ? item.owner.name : "",
                Distribuição: formatFeedDate(item.filed_at),
                Cadastro: formatFeedDate(item.captured_at),
                Importado: this.$options.filters.defaultDate(item.created_at, "DD/MM/YYYY"),
                Fonte: item.source_label,
            })), `casos-${this.tab}`);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/ui/tokens";

.pc-bulk {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 16px;
    border-bottom: 1px solid $ui-border;
    background: $ui-accent-soft;
}

.pc-export {
    margin-left: auto;
}

.pc-extras {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid $ui-border;
    background: $ui-accent-soft;
}

.pc-extras__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.pc-extras__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 8px 10px;
    border-radius: 6px;
    background: #fff;
    text-align: left;
}

.pc-extras__row + .pc-extras__row {
    margin-top: 6px;
}

button.pc-extras__row:hover {
    box-shadow: inset 0 0 0 1px $ui-border;
}
</style>
